import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formikControl/FormikControl'
import toast from 'react-hot-toast'
import { createGuardian, updateGuardian } from './api'
import { handleApiError } from '../../../helpers/errorHandler'
import { Col, Row } from 'react-bootstrap'
import {
  Country,
  getCountryCallingCode,
  isPossiblePhoneNumber,
} from 'react-phone-number-input'
import { GuardianBase, GuardianPayloadData } from './type'
import { mapGuardianApiToFormData } from './mapper'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

interface GuardianFormProps {
  initialValues?: GuardianBase
  onAfterSubmit: () => void
}

/* ---------------------------------
   VALIDATION SCHEMA
----------------------------------*/
const guardianValidationSchema = Yup.object({
  mobile: Yup.string()
    .transform(v => v ?? '') // 🔒 never null
    .test('mobile-valid', 'Enter a valid phone number', (value) => {
      if (!value) return true
      return isPossiblePhoneNumber(value)
    }),

  email: Yup.string()
    .transform(v => v ?? '')
    .email('Enter a valid email'),

  user_status: Yup.mixed<'1' | '0'>()
    .oneOf(['1', '0'], 'Status must be Active or Inactive')
    .required('Required'),

  name: Yup.string().default(''),
  gender: Yup.mixed<'Male' | 'Female' | ''>()
    .oneOf(['Male', 'Female', ''], 'Invalid gender')
    .default(''),
  dob: Yup.string().default(''),
  country: Yup.string().default(''),
  whatsapp: Yup.string().default(''),
  identification_id: Yup.string().default(''),
  address: Yup.string().default(''),
  country_code: Yup.string().default(''),
}).test(
  'mobile-or-email',
  'Either Mobile number or Email is required',
  (values) => Boolean(values?.mobile || values?.email)
)

/* ---------------------------------
   DEFAULT VALUES
----------------------------------*/
const defaultValues: GuardianPayloadData = {
  mobile: '',
  email: '',
  user_status: '0',
  name: '',
  gender: '',
  dob: '',
  country: '',
  whatsapp: '',
  identification_id: '',
  address: '',
  country_code: '',
}

function GuardianForm({ initialValues, onAfterSubmit }: GuardianFormProps) {
  const formType = initialValues ? 'update' : 'add'

  const submitHandler = async (
    values: GuardianPayloadData,
    { setSubmitting }: FormikHelpers<GuardianPayloadData>
  ) => {
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createGuardian : updateGuardian

    // ✅ remove country code before submit (backend format)
    if (values.mobile && values.country_code) {
      values.mobile = values.mobile.replace(
        `+${values.country_code}`,
        ''
      )
    }

    try {
      await action(values)
      toast.success('Success', { id: toastId })
      onAfterSubmit()
    } catch (error: any) {
      handleApiError(error, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  /* ---------------------------------
     INITIAL VALUES (DISPLAY FORMAT)
     +974xxxxxxxx shown in UI
  ----------------------------------*/
  const formValues = initialValues
    ? mapGuardianApiToFormData({
        ...initialValues,
        parent_mobile: initialValues.parent_mobile
          ? `+${initialValues.parent_country_code}${initialValues.parent_mobile}`
          : '',
      })
    : defaultValues

  return (
    <div className="px-5 py-3">
      <Formik
        initialValues={formValues}
        validationSchema={guardianValidationSchema}
        onSubmit={submitHandler}
      >
        {({ isSubmitting, resetForm, setFieldValue, dirty, isValid }) => (
          <Form>
            <div className="fs-4 mb-5 pb-2 border-bottom">
              Account Details
            </div>

            <Row>
              <Col md="4">
                <FormikControl
                  control="phone-input"
                  label="Mobile No"
                  name="mobile"
                  placeholder="Enter Mobile number"
                  onCountryChange={(value: Country | undefined) => {
                    const code = value
                      ? getCountryCallingCode(value)
                      : ''
                    setFieldValue('country_code', code)
                  }}
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Email"
                  name="email"
                  type="email"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  required
                  label="Status"
                  name="user_status"
                  options={[
                    { key: 'Active', value: '1' },
                    { key: 'Inactive', value: '0' },
                  ]}
                />
              </Col>
            </Row>

            <div className="fs-4 my-5 pb-2 border-bottom">
              Personal Info
            </div>

            <Row>
              <Col md="4">
                <FormikControl
                  control="input"
                  label="Guardian Name"
                  name="name"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  label="Gender"
                  name="gender"
                  options={[
                    { key: 'Male', value: 'Male' },
                    { key: 'Female', value: 'Female' },
                  ]}
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Date of Birth"
                  name="dob"
                  type="date"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Country"
                  name="country"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Whatsapp No"
                  name="whatsapp"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Identification Id"
                  name="identification_id"
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  label="Address"
                  name="address"
                />
              </Col>
            </Row>

            <div className="text-center pt-4">
              <FormResetBtn
                onClick={() => resetForm()}
                disabled={isSubmitting}
                className="me-4"
              />
              <FormSubmitBtn
                disabled={!dirty || !isValid || isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default GuardianForm